import * as DialogPrimitive from '@radix-ui/react-dialog'
import { cva, VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { X } from '../icons'
import { classes } from '../utils'

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={classes(
      'fixed inset-0 z-50 bg-black/30 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className,
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

export const dialogContentVariants = cva(
  [
    'fixed left-[50%] top-[50%] z-50 grid grid-rows-[auto_1fr_auto] space-y-4 w-full max-w-lg translate-x-[-50%] translate-y-[-50%] border border-border-normal bg-background p-4 shadow duration-200 rounded-xl',
    'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
  ],
  {
    variants: {
      size: {
        xs: 'sm:max-w-[18rem]',
        sm: 'sm:max-w-[24rem]',
        md: 'sm:max-w-[30rem]',
        lg: 'sm:max-w-[36rem]',
        xl: 'sm:max-w-[52rem]',
      },
    },
  },
)

type DialogContentVariantProps = VariantProps<typeof dialogContentVariants>
type DialogContentProps = {
  showClose?: boolean
} & DialogContentVariantProps &
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, showClose, size, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={classes(
        dialogContentVariants({ size }),
        {
          'sm:max-w-lg md:max-w-xl': !size,
        },
        className,
      )}
      {...props}
      aria-describedby={props['aria-describedby'] || undefined}
    >
      {children}
      {showClose && (
        <DialogPrimitive.Close
          className="focus:ring-ring absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-fill-brand-primary data-[state=open]:text-foreground-subtle"
          data-testid="dialogCloseButton"
        >
          <X className="h-4 w-4 text-foreground-subtle" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={classes('flex flex-col space-y-2', className)} {...props} />
)
DialogHeader.displayName = 'DialogHeader'

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={classes(
      'flex flex-col-reverse gap-y-2 sm:flex-row sm:justify-end sm:gap-x-2 sm:gap-y-0',
      className,
    )}
    {...props}
  />
)
DialogFooter.displayName = 'DialogFooter'

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={classes(
      'text-lg font-medium leading-none tracking-tight text-foreground-normal',
      className,
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={classes('text-foreground-subtle', className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

const TypedDialog = ((props: React.ComponentProps<typeof Dialog>) => (
  <Dialog {...props} />
)) as typeof Dialog & {
  Trigger: typeof DialogTrigger
  Portal: typeof DialogPortal
  Close: typeof DialogClose
  Overlay: typeof DialogOverlay
  Content: typeof DialogContent
  Header: typeof DialogHeader
  Footer: typeof DialogFooter
  Title: typeof DialogTitle
  Description: typeof DialogDescription
}
TypedDialog.Trigger = DialogTrigger
TypedDialog.Portal = DialogPortal
TypedDialog.Close = DialogClose
TypedDialog.Overlay = DialogOverlay
TypedDialog.Content = DialogContent
TypedDialog.Header = DialogHeader
TypedDialog.Footer = DialogFooter
TypedDialog.Title = DialogTitle
TypedDialog.Description = DialogDescription

export { TypedDialog as Dialog }
